import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { IonicModule, ModalController } from '@ionic/angular';
import { ThemeService } from '../_core/services/theme.service';
import { SubModalComponent } from './sub-modal/sub-modal.component';
import { Match } from '../_shared/models/match.model';
import { Tie } from '../_shared/models/tie.model';
import { ConfirmationModalComponent } from '../_shared/components/timeout/confirmation-modal/confirmation-modal.component';
import { TrackerBarComponent } from '../scoreboard/tracker-bar/tracker-bar.component';
import { TrackerBarModule } from '../scoreboard/tracker-bar/tracker-bar.module';
import { TrackerBoardModule } from '../match-tracker/tracker-board/tracker-board.module';
import { TrackerBoardComponent } from '../match-tracker/tracker-board/tracker-board.component';
import { ToastService } from '../_core/services/toast.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

// Interface for substitution data
interface SubstitutionData {
  endSub?: boolean;
  undoSub?: boolean;
  sideNumber?: number;
  playerNumber?: number;
  inPlayerId?: string;
  outPlayerId?: string;
  subPlayerInfo?: any;
}

@Component({
  selector: 'app-substitution',
  templateUrl: './substitution.component.html',
  styleUrls: ['./substitution.component.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule, TrackerBarModule, TrackerBoardModule, TranslateModule]
})
export class SubstitutionComponent implements OnInit, OnChanges {

  public themeService = inject(ThemeService);
  private toastService = inject(ToastService);
  private translateService = inject(TranslateService);
  @Input({ required: true }) sideColors: any;
  @Input({ required: true }) sideName: any;
  @Input({ required: true }) match: Match;
  @Input({ required: true }) tie: Tie;
  @Input({ required: true }) rowHeight: any;
  @ViewChild('trackerBar') trackerBarComponent: TrackerBarComponent | undefined;
  @ViewChild('board') trackerBoardComponent: TrackerBoardComponent | undefined;
  public remainingSubstitute: number = 0;
  // Flag to indicate if the substitution is infinite
  // If perSet or perMatch is greater than 100, we consider it infinite
  // This is to show infinite symbol in the UI
  public isSubstituteInfinite: boolean = false;
  public isPlayerSelected: boolean = false;

  constructor(private readonly modalController: ModalController) { }

  ngOnInit() {
    // Initialize remaining substitutions when the component is created
    this.updateRemainingSubstitutions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.match?.currentValue || changes?.sideName?.currentValue) {
      this.updateRemainingSubstitutions();
    }
  }

  private updateRemainingSubstitutions(): void {
    const INFINITE_SUBSTITUTION_CHECK_NUMBER = 100;
    const policy = this.match?.substitutionPolicy || { perSet: 0, perMatch: 0 };
    const setNumber = this.match?.sets?.length || 1;
    const sideTrack = this.match?.substituteTrack?.[this.sideName] || [];

    this.isSubstituteInfinite =
      policy.perSet >= INFINITE_SUBSTITUTION_CHECK_NUMBER ||
      policy.perMatch >= INFINITE_SUBSTITUTION_CHECK_NUMBER;
    // Handle case when both substitution limits are 0
    if (policy.perMatch === 0 && policy.perSet === 0) {
      this.remainingSubstitute = -1;
      return;
    }

    const latestTracker = sideTrack.find(t => t?.setNumber === setNumber);

    if (latestTracker) {
      const remainingPerMatch = policy.perMatch - latestTracker.totalUsedInMatch;
      const remainingPerSet = Math.max(policy.perSet, latestTracker.perSet) - latestTracker.usedPerSet;
      this.remainingSubstitute = Math.min(remainingPerMatch, remainingPerSet);
    } else {
      const previousTracker = sideTrack.find(t => t?.setNumber === setNumber - 1);
      if (previousTracker) {
        const remainingPerMatch = policy.perMatch - previousTracker.totalUsedInMatch;
        this.remainingSubstitute = Math.min(policy.perSet, remainingPerMatch);
      } else {
        this.remainingSubstitute = Math.min(policy.perSet, policy.perMatch);
      }

      this.remainingSubstitute = Math.max(this.remainingSubstitute, 0);
    }
  }

  // Constants for modal configuration
  private readonly MODAL_BREAKPOINTS = [0, 0.5, 1];
  private readonly INITIAL_BREAKPOINT = 1;
  private readonly SUBSTITUTION_SETUP_TITLE = "Do you want to setup substitution?";

  /**
   * Shows the substitution modal for the specified side
   * @param sideNumber - The side number for substitution
   */
  async showSubstitutionModal(sideNumber: number): Promise<void> {
    try {
      if (!this.tie) {
        console.warn('Tie data not available for individual match');
        this.toastService.presentDangerToast(this.translateService.instant('Tie data not available for individual match'));
        return;
      }

      if (this.shouldShowSubstitutionSetup()) {
        await this.showSubstitutionSetupModal();
        return;
      }

      await this.showSubstitutionPlayerModal(sideNumber);
    } catch (error) {
      console.error('Error showing substitution modal:', error);
      // Handle error appropriately - could show a toast or alert
      this.toastService.presentDangerToast(this.translateService.instant('Error showing substitution modal'));
    }
  }

  /**
   * Checks if substitution setup modal should be shown
   */
  private shouldShowSubstitutionSetup(): boolean {
    return !this.match.substitutionPolicy || 
           (this.match.substitutionPolicy.perMatch === 0 && 
            this.match.substitutionPolicy.perSet === 0);
  }

  /**
   * Shows the substitution setup confirmation modal
   */
  private async showSubstitutionSetupModal(): Promise<void> {
    const modal = await this.modalController.create({
      component: ConfirmationModalComponent,
      componentProps: {
        title: this.SUBSTITUTION_SETUP_TITLE,
        onConfirm: () => this.trackerBarComponent?.manageSubstitution(),
      },
    });
    
    await modal.present();
  }

  /**
   * Shows the substitution player selection modal
   */
  private async showSubstitutionPlayerModal(sideNumber: number): Promise<void> {
    const modal = await this.modalController.create({
      component: SubModalComponent,
      componentProps: {
        match: this.match,
        tie: this.tie,
        sideNumber: sideNumber,
        onSubPlayerSelected: async (player: any) => {
          this.isPlayerSelected = true;
          await this.trackerBoardComponent!.startSubstitution(sideNumber);
        }
      },
      backdropDismiss: true,
      breakpoints: this.MODAL_BREAKPOINTS,
      initialBreakpoint: this.INITIAL_BREAKPOINT,
      handle: true,
    });

    await modal.present();
    await this.handleModalResult(modal, sideNumber);
  }

  /**
   * Handles the modal result and performs appropriate actions
   */
  private async handleModalResult(modal: any, sideNumber: number): Promise<void> {
    const result = await modal.onWillDismiss();
    
    if (result?.data) {
      await this.handleModalData(result.data as SubstitutionData);
    } else {
      await this.handleModalDismissal(sideNumber);
    }
  }

  /**
   * Handles modal data and performs substitution actions
   */
  private async handleModalData(data: SubstitutionData): Promise<void> {
    if (data?.endSub) {
      await this.endSubstitution(data);
    }

    if (data?.undoSub) {
      this.modalController.dismiss();
    }
  }

  /**
   * Ends the substitution process
   */
  private async endSubstitution(data: SubstitutionData): Promise<void> {
    await this.trackerBoardComponent!.endSubstitution(
      data.sideNumber!,
      data.playerNumber!,
      data.inPlayerId!,
      data.outPlayerId!,
      data.subPlayerInfo
    );
  }

  /**
   * Handles modal dismissal and cleans up substitution state
   */
  private async handleModalDismissal(sideNumber: number): Promise<void> {
    if (this.isPlayerSelected) {
      await this.trackerBoardComponent!.dismissSubstitution(sideNumber);
      this.isPlayerSelected = false;
    }
  }
}
